import React, { useEffect, useMemo, useState } from 'react';
import { Button, SideBarModal } from '@/components';
import { SearchableDropdown } from '@/components/SearchableDropdown';
import { Model, Oem } from '@/types/common';
import {
  BUSINESS_UNIT_LABEL,
  BusinessUnit,
  COMPANY_ENTITY_OPTIONS,
  CompanyEntity,
  CreatePurchaseOrderBody,
} from '@/types/purchase-order';
import { Provider } from '@/services/api/providers';
import { fileToBase64, getBucketUrlFile, uploadTempFile } from '@/utils';

interface CreatePurchaseOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreatePurchaseOrderBody) => void;
  isLoading?: boolean;
  oems: Oem[];
  models: Model[];
  clients: Provider[];
}

interface FormData {
  code: string;
  oemID: string;
  modelID: string;
  description: string;
  totalUnits: string;
  entity: CompanyEntity | '';
  requestingBusinessUnit: BusinessUnit | '';
  clientID: string;
  clientName: string;
  notes: string;
}

const INITIAL_FORM: FormData = {
  code: '',
  oemID: '',
  modelID: '',
  description: '',
  totalUnits: '',
  entity: '',
  requestingBusinessUnit: '',
  clientID: '',
  clientName: '',
  notes: '',
};

const BUSINESS_UNIT_OPTIONS = Object.values(BusinessUnit).map((value) => ({
  value,
  label: BUSINESS_UNIT_LABEL[value],
}));

// Shared upload service accepts a fixed file_type enum; PO demand evidence
// rides in under 'vehicle' (same precedent as STNK). Allowed formats below.
const EVIDENCE_FORMATS = ['pdf', 'png', 'jpg', 'jpeg', 'webp'];
const EVIDENCE_MAX_BYTES = 10 * 1024 * 1024; // 10MB

const inputClass = (hasError?: boolean) =>
  `w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${
    hasError ? 'border-red-500' : 'border-gray-300'
  }`;

const CreatePurchaseOrderModal: React.FC<CreatePurchaseOrderModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  oems,
  models,
  clients,
}) => {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>(
    {}
  );

  // Demand evidence (optional) — uploaded to the shared bucket; we keep the URL.
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [evidenceName, setEvidenceName] = useState('');
  const [evidenceError, setEvidenceError] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(INITIAL_FORM);
      setErrors({});
      setEvidenceUrl('');
      setEvidenceName('');
      setEvidenceError('');
      setIsUploading(false);
    }
  }, [isOpen]);

  const filteredModels = useMemo(() => {
    if (!formData.oemID) return models;
    return models.filter((model) => model.oemID === formData.oemID);
  }, [models, formData.oemID]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Reset model when OEM changes
      if (field === 'oemID') {
        next.modelID = '';
      }
      return next;
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleClientChange = (clientId: string) => {
    const selectedClient = clients.find(
      (client) => (client.ID ?? client.id).toString() === clientId
    );
    setFormData((prev) => ({
      ...prev,
      clientID: clientId,
      clientName: selectedClient?.name || '',
    }));
  };

  const handleEvidenceChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    // Allow re-selecting the same file later
    e.target.value = '';
    if (!file) return;

    setEvidenceError('');
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!EVIDENCE_FORMATS.includes(ext)) {
      setEvidenceError('File must be a PDF or image (pdf, png, jpg, jpeg, webp)');
      return;
    }
    if (file.size > EVIDENCE_MAX_BYTES) {
      setEvidenceError('File is too large (max 10MB)');
      return;
    }

    setIsUploading(true);
    try {
      const uuid = crypto.randomUUID();
      const bucketRes = await getBucketUrlFile({
        uuid,
        fileType: 'vehicle',
        fileFormat: ext,
      });
      if (!bucketRes?.data) {
        throw new Error('Failed to get upload URL');
      }
      const uploadUrl = bucketRes.data;
      const base64Data = await fileToBase64(file);
      const uploadRes = await uploadTempFile(
        uploadUrl,
        base64Data,
        file.name,
        file.type
      );
      if (uploadRes.status !== 200) {
        throw new Error('Upload failed');
      }
      setEvidenceUrl(uploadUrl);
      setEvidenceName(file.name);
    } catch (error) {
      setEvidenceError('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const removeEvidence = () => {
    setEvidenceUrl('');
    setEvidenceName('');
    setEvidenceError('');
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.code.trim()) newErrors.code = 'Purchase Order ID is required';
    if (!formData.oemID) newErrors.oemID = 'OEM is required';
    if (!formData.modelID) newErrors.modelID = 'Model is required';
    if (!formData.description.trim())
      newErrors.description = 'Description / Justification is required';

    const totalUnits = Number(formData.totalUnits);
    if (!formData.totalUnits.trim()) {
      newErrors.totalUnits = 'Total units is required';
    } else if (!Number.isInteger(totalUnits) || totalUnits < 1) {
      newErrors.totalUnits = 'Total units must be an integer of at least 1';
    }

    if (!formData.entity) newErrors.entity = 'Entity is required';

    if (!formData.requestingBusinessUnit)
      newErrors.requestingBusinessUnit = 'Business unit is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (isUploading) return;

    onSubmit({
      code: formData.code.trim(),
      oemID: formData.oemID,
      modelID: formData.modelID,
      description: formData.description.trim(),
      totalUnits: Number(formData.totalUnits),
      entity: formData.entity as CompanyEntity,
      requestingBusinessUnit: formData.requestingBusinessUnit as BusinessUnit,
      ...(formData.notes.trim() ? { notes: formData.notes.trim() } : {}),
      ...(formData.clientID
        ? {
            clientID: Number(formData.clientID),
            clientName: formData.clientName,
          }
        : {}),
      ...(evidenceUrl ? { demandEvidenceFileUrl: evidenceUrl } : {}),
      createdBy: '',
    });
  };

  if (!isOpen) return null;

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      position="right"
      width="sm"
      header={
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Create Purchase Order
          </h2>
          <p className="text-sm font-normal text-gray-600 mt-1">
            Create a new vehicle purchase order.
          </p>
        </div>
      }
      footer={
        <Button
          variant="primary"
          onClick={() =>
            handleSubmit({ preventDefault: () => {} } as React.FormEvent)
          }
          disabled={isLoading || isUploading}
          className="w-full py-3"
        >
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="bg-gray-50 px-6 py-2 font-medium rounded-t-lg heading-field">
          Detail
        </div>
        <div className="space-y-3 text-sm px-6 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Order ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => handleInputChange('code', e.target.value)}
              placeholder="e.g. PO-2026-001"
              className={inputClass(!!errors.code)}
            />
            {errors.code && (
              <p className="text-red-500 text-xs mt-1">{errors.code}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              OEM <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              placeholder="Select OEM"
              options={oems.map((oem) => ({ value: oem.ID, label: oem.name }))}
              value={formData.oemID}
              onChange={(value) => handleInputChange('oemID', value)}
              error={!!errors.oemID}
            />
            {errors.oemID && (
              <p className="text-red-500 text-xs mt-1">{errors.oemID}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Model <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              placeholder={
                formData.oemID ? 'Select model' : 'Select an OEM first'
              }
              options={filteredModels.map((model) => ({
                value: model.ID,
                label: model.name,
              }))}
              value={formData.modelID}
              onChange={(value) => handleInputChange('modelID', value)}
              disabled={!formData.oemID}
              error={!!errors.modelID}
            />
            {errors.modelID && (
              <p className="text-red-500 text-xs mt-1">{errors.modelID}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Units <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min={1}
              step={1}
              value={formData.totalUnits}
              onChange={(e) => handleInputChange('totalUnits', e.target.value)}
              placeholder="e.g. 50"
              className={inputClass(!!errors.totalUnits)}
            />
            {errors.totalUnits && (
              <p className="text-red-500 text-xs mt-1">{errors.totalUnits}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Entity <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              placeholder="Select entity"
              options={COMPANY_ENTITY_OPTIONS}
              value={formData.entity}
              onChange={(value) => handleInputChange('entity', value)}
              error={!!errors.entity}
            />
            {errors.entity && (
              <p className="text-red-500 text-xs mt-1">{errors.entity}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Unit <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              placeholder="Select business unit"
              options={BUSINESS_UNIT_OPTIONS}
              value={formData.requestingBusinessUnit}
              onChange={(value) =>
                handleInputChange('requestingBusinessUnit', value)
              }
              error={!!errors.requestingBusinessUnit}
            />
            {errors.requestingBusinessUnit && (
              <p className="text-red-500 text-xs mt-1">
                {errors.requestingBusinessUnit}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client{' '}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <SearchableDropdown
              placeholder="Search clients or leave blank for buffer / general fleet"
              options={clients.map((client) => ({
                value: (client.ID ?? client.id).toString(),
                label: client.name,
              }))}
              value={formData.clientID}
              onChange={handleClientChange}
              emptyMessage="No clients found"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description / Justification{' '}
              <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                handleInputChange('description', e.target.value)
              }
              placeholder="Describe the order and why these units are needed now."
              rows={3}
              className={inputClass(!!errors.description)}
            />
            {errors.description && (
              <p className="text-red-500 text-xs mt-1">{errors.description}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Demand Evidence{' '}
              <span className="text-gray-400 font-normal">
                (optional — 1 PDF or image)
              </span>
            </label>
            {evidenceUrl ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm">
                <span className="inline-flex min-w-0 items-center gap-1 text-gray-700">
                  <i className="ri-attachment-2 flex-shrink-0" />
                  <span className="truncate">{evidenceName}</span>
                </span>
                <button
                  type="button"
                  onClick={removeEvidence}
                  className="flex-shrink-0 text-xs font-medium text-red-500 hover:underline"
                >
                  Remove
                </button>
              </div>
            ) : (
              <label
                className={`flex cursor-pointer items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm ${
                  isUploading
                    ? 'border-gray-300 text-gray-400'
                    : 'border-gray-300 text-gray-600 hover:border-purple-400'
                }`}
              >
                <i className="ri-upload-2-line" />
                {isUploading ? 'Uploading...' : 'Upload PDF / image'}
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                  onChange={handleEvidenceChange}
                  disabled={isUploading}
                  className="hidden"
                />
              </label>
            )}
            {evidenceError && (
              <p className="text-red-500 text-xs mt-1">{evidenceError}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Buffer rationale, spreadsheet links, delivery batches…"
              rows={2}
              className={inputClass(false)}
            />
          </div>
        </div>
      </form>
    </SideBarModal>
  );
};

export default CreatePurchaseOrderModal;
